import type { MouseEvent } from 'react'

import type { Page } from '@subaio/utils'

import { useRoutingContext } from './useRoutingContext'

const trailingSlashRegex = /\/(?=$|\?|#)/

/** Remove trailing slash to avoid mismatches */
const normalizePath = (path: string) => path.replace(trailingSlashRegex, '')

const getIsActive = (link: string, location: string, match: 'parent' | 'path' | 'exact' | 'page') => {
	switch (match) {
		case 'parent':
			return link === location || location.startsWith(link)
		case 'path':
			return link === location.split('?')[0]
		case 'page':
			return link === location.split('#')[0] || link.split('#')[0] === location.split('#')[0]
		case 'exact':
			return link === location
	}
}

type Options = {
	/**
	 * Replace current page instead of pushing to the navigation stack
	 */
	replace?: boolean
	/**
	 * Precision of active path matching (defaults to `'parent'`)
	 * - `'parent'` matches if linked path is a parent of the current, ie. `/a`
	 *   is active when on `/a/b`
	 * - `'path'` matches entire path, but ignores queries
	 * - `'page'` matches entire path, but ignores fragments
	 * - `'exact'` requires an exact match
	 */
	activeMatch?: 'parent' | 'path' | 'page' | 'exact'
}

export const useLink = (page: Page, { replace = false, activeMatch = 'parent' }: Options = {}) => {
	const { getPath, replace: replaceUrl, push, url } = useRoutingContext()

	const href = getPath(page)

	if (href === null) {
		throw new Error(`@subaio/components useLink: Invalid route ${page.name}`)
	}

	return {
		href,
		onClick: (event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
			// Check if trying to open in new window, and let browser handle it
			if (event.button === 2 || event.metaKey || event.ctrlKey) return

			event.preventDefault()
			if (replace) {
				replaceUrl(page)
			} else {
				push(page)
			}
		},
		isActive: getIsActive(normalizePath(href), normalizePath(url ?? ''), activeMatch),
	}
}
